import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserPlus } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { useSiteContent } from "@/lib/useSiteContent";
import { keysFor } from "@/config/cmsSections";

export default function Register() {
    const navigate = useNavigate();
    const { register } = useAuth();
    const c = useSiteContent(keysFor("register"));
    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
        audience: "particulier",
        company: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError("");
        const payload = { ...form };
        if (form.audience === "particulier") delete payload.company;
        const res = await register(payload);
        setSubmitting(false);
        if (res.ok) {
            toast.success("Bienvenue sur Energy Market !");
            navigate("/dashboard");
        } else {
            setError(res.error);
        }
    };

    return (
        <div className="em-container py-20 max-w-md" data-testid="register-page">
            <p className="em-eyebrow">{c("register.hero.eyebrow", "Créer mon compte")}</p>
            <h1 className="font-display text-4xl md:text-5xl font-black text-primary mt-2">{c("register.hero.title", "Inscription")}</h1>
            <p className="text-muted-foreground mt-3">{c("register.hero.subtitle", "Suivez vos économies, vos contrats et vos analyses depuis votre espace personnel.")}</p>

            <Card className="rounded-3xl border-border mt-8">
                <CardContent className="p-7">
                    <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form">
                        <Tabs value={form.audience} onValueChange={(v) => update("audience", v)}>
                            <TabsList className="w-full rounded-full">
                                <TabsTrigger value="particulier" className="flex-1 rounded-full" data-testid="register-audience-particulier">{c("register.audience_b2c", "Particulier")}</TabsTrigger>
                                <TabsTrigger value="entreprise" className="flex-1 rounded-full" data-testid="register-audience-entreprise">{c("register.audience_b2b", "Entreprise")}</TabsTrigger>
                            </TabsList>
                        </Tabs>

                        <div>
                            <Label htmlFor="name">{c("register.form.label_name", "Nom complet")}</Label>
                            <Input id="name" required value={form.name} onChange={(e) => update("name", e.target.value)} className="rounded-xl mt-2" data-testid="register-name" />
                        </div>

                        {form.audience === "entreprise" && (
                            <div>
                                <Label htmlFor="company">{c("register.form.label_company", "Société")}</Label>
                                <Input id="company" value={form.company} onChange={(e) => update("company", e.target.value)} className="rounded-xl mt-2" data-testid="register-company" />
                            </div>
                        )}

                        <div>
                            <Label htmlFor="email">{c("register.form.label_email", "Email")}</Label>
                            <Input id="email" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="rounded-xl mt-2" data-testid="register-email" />
                        </div>
                        <div>
                            <Label htmlFor="password">{c("register.form.label_password", "Mot de passe (6 caractères min.)")}</Label>
                            <Input id="password" type="password" required minLength={6} value={form.password} onChange={(e) => update("password", e.target.value)} className="rounded-xl mt-2" data-testid="register-password" />
                        </div>
                        {error && (
                            <p className="text-sm text-destructive" data-testid="register-error">{error}</p>
                        )}
                        <Button type="submit" disabled={submitting} className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold h-11" data-testid="register-submit">
                            <UserPlus className="h-4 w-4 mr-2" />
                            {submitting ? "Création…" : c("register.form.submit", "Créer mon compte")}
                        </Button>
                    </form>
                    <p className="text-sm text-muted-foreground mt-5 text-center">
                        {c("register.footer.text", "Déjà membre ?")}{" "}
                        <Link to="/connexion" className="text-primary font-semibold hover:underline" data-testid="register-to-login">{c("register.footer.link", "Se connecter")}</Link>
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}
